// src/services/diagnosticEngine.ts
// PCCheckr 80-Check Diagnostic Engine
// Evaluates all 80 checks against parsed .pcc data

import type { ParsedScanData } from '../utils/PCCUtils';

// ============================================================================
// TYPES
// ============================================================================

export type Severity = 'healthy' | 'low' | 'medium' | 'high' | 'critical';
export type Tier = 'free' | 'pro';

export interface CheckResult {
  id: string;
  category: string;
  name: string;
  status: 'pass' | 'warning' | 'fail' | 'not_detected';
  severity: Severity;
  scoreImpact: number;
  value: string | number | null;
  evidence: string;
  explanation: string;
  recommendation: string;
  tier: Tier;
}

export interface CategoryScore {
  category: string;
  score: number;
  maxScore: 100;
  weight: number;
  checks: CheckResult[];
  passCount: number;
  warningCount: number;
  failCount: number;
}

export interface DiagnosticReport {
  scanId: string;
  scanTimestamp: string;
  totalChecks: 80;
  passCount: number;
  warningCount: number;
  criticalCount: number;
  notDetectedCount: number;
  healthScore: number;
  scoreStatus: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  primaryBottleneck: string;
  bottleneckExplanation: string;
  categoryScores: CategoryScore[];
  allChecks: CheckResult[];
  criticalFindings: CheckResult[];
  warningFindings: CheckResult[];
  passedChecks: CheckResult[];
  fixPlan: FixStep[];
  manufacturer: string;
  model: string;
}

export interface FixStep {
  order: number;
  title: string;
  timeMinutes: number;
  difficulty: 'easy' | 'medium' | 'hard';
  description: string;
  steps: string[];
  relatedCheckIds: string[];
}

// ============================================================================
// CATEGORY WEIGHTS (for weighted health score)
// ============================================================================

const CATEGORY_WEIGHTS: Record<string, number> = {
  'Storage': 0.18,
  'RAM': 0.15,
  'BSOD/Crashes': 0.15,
  'Startup': 0.10,
  'CPU': 0.08,
  'Network/WiFi': 0.10,
  'Windows Health': 0.08,
  'Battery': 0.05,
  'Drivers': 0.05,
  'Security': 0.04,
  'Event Viewer': 0.02,
  'Performance Bottlenecks': 0.00, // informational
  'System Overview': 0.00, // informational
};

// ============================================================================
// HELPERS
// ============================================================================

function safeGet<T>(obj: any, path: string, fallback: T): any {
  try {
    const result = path.split('.').reduce((o, k) => {
      if (o === null || o === undefined) return undefined;
      // Handle array indices like [0]
      const arrayMatch = k.match(/^(\w+)\[(\d+)\]$/);
      if (arrayMatch) {
        return o[arrayMatch[1]]?.[parseInt(arrayMatch[2])];
      }
      return o[k];
    }, obj);
    return (result === undefined || result === null) ? fallback : result;
  } catch {
    return fallback;
  }
}

function makeCheck(
  id: string,
  category: string,
  name: string,
  options: Partial<CheckResult>
): CheckResult {
  return {
    id,
    category,
    name,
    status: 'pass',
    severity: 'healthy',
    scoreImpact: 0,
    value: null,
    evidence: '',
    explanation: '',
    recommendation: '',
    tier: 'pro',
    ...options,
  };
}

function severityToScore(severity: Severity): number {
  switch (severity) {
    case 'critical': return -25;
    case 'high': return -15;
    case 'medium': return -8;
    case 'low': return -3;
    default: return 0;
  }
}

// ============================================================================
// CHECK EVALUATORS (80 functions, one per check)
// ============================================================================

// ─── SYSTEM OVERVIEW (8 checks) ───────────────────────────────────────────

function checkSYS001(data: any): CheckResult {
  const os = safeGet(data, 'system.os', safeGet(data, 'system.os_edition', 'Unknown'));
  return makeCheck('SYS-001', 'System Overview', 'Operating System', {
    status: os === 'Unknown' ? 'not_detected' : 'pass',
    value: os,
    evidence: `OS Edition: ${os}`,
    explanation: `Your system is running ${os}`,
    tier: 'free',
  });
}

function checkSYS002(data: any): CheckResult {
  const build = parseInt(safeGet(data, 'system.os_build', '0'));
  const isOutdated = (build > 0 && build < 19044) || (build >= 22000 && build < 22631);
  return makeCheck('SYS-002', 'System Overview', 'Windows Build', {
    status: build === 0 ? 'not_detected' : isOutdated ? 'warning' : 'pass',
    severity: isOutdated ? 'medium' : 'healthy',
    scoreImpact: isOutdated ? -8 : 0,
    value: build,
    evidence: `Build number: ${build}`,
    explanation: isOutdated
      ? 'Your Windows build is older than current supported version.'
      : 'Windows build is current.',
    recommendation: isOutdated ? 'Run Windows Update to install the latest feature update.' : '',
  });
}

function checkSYS003(data: any): CheckResult {
  const mfr = safeGet(data, 'system.manufacturer', 'Unknown');
  return makeCheck('SYS-003', 'System Overview', 'Manufacturer', {
    status: mfr === 'Unknown' ? 'not_detected' : 'pass',
    value: mfr,
    evidence: `System manufacturer: ${mfr}`,
    explanation: `Manufacturer detected: ${mfr}. Enables vendor-specific recommendations.`,
  });
}

function checkSYS004(data: any): CheckResult {
  const model = safeGet(data, 'system.model', 'Unknown');
  return makeCheck('SYS-004', 'System Overview', 'Model', {
    status: model === 'Unknown' ? 'not_detected' : 'pass',
    value: model,
    evidence: `Model: ${model}`,
    explanation: `Device model: ${model}`,
  });
}

function checkSYS005(data: any): CheckResult {
  const days = safeGet<number>(data, 'system.uptime_days', 0);
  let severity: Severity = 'healthy';
  let status: CheckResult['status'] = 'pass';
  if (days > 14) { severity = 'high'; status = 'fail'; }
  else if (days > 7) { severity = 'medium'; status = 'warning'; }
  else if (days > 3) { severity = 'low'; status = 'warning'; }

  return makeCheck('SYS-005', 'System Overview', 'System Uptime', {
    status,
    severity,
    scoreImpact: severityToScore(severity),
    value: days,
    evidence: `PC running for ${days} days without restart`,
    explanation: days > 3
      ? `Your PC has been running ${days} days without restart. Restarts clear memory leaks and apply pending updates.`
      : `PC restart recent (${days} days ago).`,
    recommendation: days > 3 ? 'Restart your PC: Start > Power > Restart' : '',
    tier: 'free',
  });
}

function checkSYS006(data: any): CheckResult {
  const sysType = safeGet(data, 'system.system_type', safeGet(data, 'system.type', ''));
  const is32bit = sysType.includes('32');
  return makeCheck('SYS-006', 'System Overview', 'System Architecture', {
    status: is32bit ? 'fail' : sysType ? 'pass' : 'not_detected',
    severity: is32bit ? 'high' : 'healthy',
    scoreImpact: is32bit ? -15 : 0,
    value: sysType,
    evidence: `System type: ${sysType}`,
    explanation: is32bit
      ? 'Your Windows is 32-bit. Modern apps require 64-bit Windows for full performance.'
      : `Running ${sysType}`,
    recommendation: is32bit ? 'Consider reinstalling Windows as 64-bit if hardware supports it.' : '',
    tier: 'free',
  });
}

function checkSYS007(data: any): CheckResult {
  const tpm = safeGet(data, 'system.tpm', 'Unknown');
  const enabled = tpm.includes('Enabled') || tpm === 'Ready';
  return makeCheck('SYS-007', 'System Overview', 'TPM Security Chip', {
    status: tpm === 'Unknown' || tpm === 'Not Available' ? 'not_detected' : enabled ? 'pass' : 'warning',
    severity: enabled ? 'healthy' : 'low',
    scoreImpact: enabled ? 0 : -3,
    value: tpm,
    evidence: `TPM status: ${tpm}`,
    explanation: enabled
      ? 'TPM is enabled and ready.'
      : 'TPM is not enabled. Required for some Windows 11 features and BitLocker.',
    recommendation: enabled ? '' : 'Enable TPM in BIOS settings if available.',
  });
}

function checkSYS008(data: any): CheckResult {
  const sb = safeGet(data, 'system.secure_boot', 'Unknown');
  const enabled = sb === 'Enabled' || sb === true;
  return makeCheck('SYS-008', 'System Overview', 'Secure Boot', {
    status: sb === 'Unknown' ? 'not_detected' : enabled ? 'pass' : 'warning',
    severity: enabled ? 'healthy' : 'low',
    scoreImpact: enabled ? 0 : -3,
    value: sb,
    evidence: `Secure Boot: ${sb}`,
    explanation: enabled
      ? 'Secure Boot is enabled, providing boot-time malware protection.'
      : 'Secure Boot is disabled.',
    recommendation: enabled ? '' : 'Consider enabling Secure Boot in BIOS for enhanced security.',
  });
}

// ─── CPU (6 checks) ───────────────────────────────────────────

function checkCPU001(data: any): CheckResult {
  const model = safeGet(data, 'cpu.model', 'Unknown');
  return makeCheck('CPU-001', 'CPU', 'Processor Model', {
    status: model === 'Unknown' ? 'not_detected' : 'pass',
    value: model,
    evidence: model,
    explanation: `CPU: ${model}`,
  });
}

function checkCPU002(data: any): CheckResult {
  const load = safeGet<number>(data, 'cpu.load_pct', 0);
  let severity: Severity = 'healthy';
  if (load > 90) severity = 'high';
  else if (load > 75) severity = 'medium';
  else if (load > 60) severity = 'low';

  const topProc = safeGet(data, 'cpu.top_processes[0].name', 'unknown');

  return makeCheck('CPU-002', 'CPU', 'CPU Load', {
    status: load > 60 ? (load > 90 ? 'fail' : 'warning') : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: load,
    evidence: `CPU at ${load}%, top consumer: ${topProc}`,
    explanation: load > 60
      ? `CPU is running at ${load}%. ${topProc} is the top consumer.`
      : `CPU load is normal (${load}%).`,
    recommendation: load > 75 ? `Close ${topProc} if not needed, or check Task Manager.` : '',
  });
}

function checkCPU003(data: any): CheckResult {
  const cores = safeGet<number>(data, 'cpu.cores', 0);
  const isLow = cores > 0 && cores < 4;
  return makeCheck('CPU-003', 'CPU', 'CPU Cores', {
    status: cores === 0 ? 'not_detected' : isLow ? 'warning' : 'pass',
    severity: isLow ? 'low' : 'healthy',
    scoreImpact: isLow ? -3 : 0,
    value: cores,
    evidence: `${cores} physical cores`,
    explanation: isLow
      ? `Only ${cores} CPU cores. Modern multitasking benefits from 4+ cores.`
      : `${cores} cores — adequate for modern workloads.`,
  });
}

function checkCPU004(data: any): CheckResult {
  const speed = safeGet<number>(data, 'cpu.max_speed_ghz', 0);
  return makeCheck('CPU-004', 'CPU', 'CPU Speed', {
    status: speed === 0 ? 'not_detected' : 'pass',
    value: speed,
    evidence: `${speed} GHz max clock`,
    explanation: `CPU maximum speed: ${speed} GHz`,
  });
}

function checkCPU005(data: any): CheckResult {
  const top = safeGet<any>(data, 'cpu.top_processes[0]', null);
  const heavyCpu = top && safeGet<number>(top, 'cpu_sec', 0) > 30;
  return makeCheck('CPU-005', 'CPU', 'Top CPU Consumer', {
    status: !top ? 'not_detected' : heavyCpu ? 'warning' : 'pass',
    severity: heavyCpu ? 'medium' : 'healthy',
    scoreImpact: heavyCpu ? -8 : 0,
    value: top ? `${top.name} (${top.cpu_sec}s)` : null,
    evidence: top ? `${top.name} using ${top.cpu_sec} seconds CPU time` : 'No top consumer data',
    explanation: heavyCpu
      ? `${top.name} has used significant CPU time. May be slowing your PC.`
      : 'No process dominating CPU usage.',
  });
}

function checkCPU006(data: any): CheckResult {
  // Map to stability thermal events if available
  const events = safeGet<number>(data, 'stability.thermal_events_count', 0);
  let severity: Severity = 'healthy';
  if (events > 5) severity = 'high';
  else if (events > 0) severity = 'medium';

  return makeCheck('CPU-006', 'CPU', 'CPU Thermal Throttling', {
    status: events === 0 ? 'pass' : events > 5 ? 'fail' : 'warning',
    severity,
    scoreImpact: severityToScore(severity),
    value: events,
    evidence: `${events} thermal events`,
    explanation: events > 0
      ? `CPU throttled ${events} times due to heat. Check cooling and dust.`
      : 'No thermal throttling detected.',
    recommendation: events > 0 ? 'Clean dust from vents, check fan operation.' : '',
  });
}

// ─── RAM (8 checks) ───────────────────────────────────────────

function checkRAM001(data: any): CheckResult {
  const total = safeGet<number>(data, 'ram.totalGB', safeGet<number>(data, 'ram.total_gb', 0));
  let severity: Severity = 'healthy';
  let status: CheckResult['status'] = 'pass';
  if (total > 0 && total < 4) { severity = 'critical'; status = 'fail'; }
  else if (total > 0 && total < 8) { severity = 'high'; status = 'fail'; }
  else if (total < 16 && total > 0) { severity = 'low'; status = 'warning'; }

  return makeCheck('RAM-001', 'RAM', 'Total RAM', {
    status: total === 0 ? 'not_detected' : status,
    severity,
    scoreImpact: severityToScore(severity),
    value: total > 0 ? `${total} GB` : 'Unknown',
    evidence: `${total} GB RAM installed`,
    explanation: total < 8 && total > 0
      ? `Your PC has only ${total} GB RAM. Modern Windows + apps need 8-16 GB minimum.`
      : total > 0 ? `${total} GB RAM is adequate for most uses.` : 'RAM information not detected.',
    recommendation: total < 8 && total > 0 ? `Consider upgrading RAM to 16 GB.` : '',
    tier: 'free',
  });
}

function checkRAM002(data: any): CheckResult {
  const pct = safeGet<number>(data, 'ram.used_pct', 0);
  let severity: Severity = 'healthy';
  if (pct > 90) severity = 'critical';
  else if (pct > 75) severity = 'high';
  else if (pct > 60) severity = 'medium';

  const top = safeGet(data, 'ram.top_processes[0].name', 'unknown');
  const topMb = safeGet<number>(data, 'ram.top_processes[0].ram_mb', 0);

  return makeCheck('RAM-002', 'RAM', 'RAM Usage', {
    status: pct === 0 ? 'not_detected' : pct > 60 ? (pct > 90 ? 'fail' : 'warning') : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: pct,
    evidence: `RAM at ${pct}%, top consumer: ${top} using ${topMb} MB`,
    explanation: pct > 75
      ? `RAM is ${pct}% full. ${top} is using ${topMb} MB.`
      : `RAM usage normal (${pct}%).`,
    recommendation: pct > 75 ? `Close ${top} or restart your PC to free memory.` : '',
  });
}

function checkRAM003(data: any): CheckResult {
  const avail = safeGet<number>(data, 'ram.available_gb', 0);
  let severity: Severity = 'healthy';
  if (avail < 0.5) severity = 'critical';
  else if (avail < 1.5) severity = 'high';

  return makeCheck('RAM-003', 'RAM', 'Available RAM', {
    status: avail === 0 ? 'not_detected' : avail < 1.5 ? 'fail' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: avail,
    evidence: `${avail} GB available`,
    explanation: avail < 1.5
      ? `Only ${avail} GB free. Windows needs 2+ GB free to work smoothly.`
      : `${avail} GB available — sufficient.`,
  });
}

function checkRAM004(data: any): CheckResult {
  const used = safeGet<number>(data, 'ram.slots_used', 0);
  const total = safeGet<number>(data, 'ram.slots_total', 0);
  return makeCheck('RAM-004', 'RAM', 'RAM Slot Configuration', {
    status: total === 0 ? 'not_detected' : 'pass',
    value: `${used}/${total}`,
    evidence: `${used} of ${total} RAM slots in use`,
    explanation: `${used} RAM modules installed in ${total} available slots.`,
  });
}

function checkRAM005(data: any): CheckResult {
  const free = safeGet<number>(data, 'ram.slots_free', 0);
  const totalGb = safeGet<number>(data, 'ram.total_gb', 0);
  const upgradeOpportunity = free > 0 && totalGb <= 8;

  return makeCheck('RAM-005', 'RAM', 'RAM Upgrade Potential', {
    status: upgradeOpportunity ? 'warning' : 'pass',
    severity: upgradeOpportunity ? 'medium' : 'healthy',
    scoreImpact: upgradeOpportunity ? -8 : 0,
    value: free,
    evidence: `${free} empty RAM slots, currently ${totalGb} GB total`,
    explanation: upgradeOpportunity
      ? `You have ${free} empty RAM slot(s). With only ${totalGb} GB installed, adding RAM would significantly improve performance.`
      : `No upgrade slots available or RAM is already adequate.`,
    recommendation: upgradeOpportunity ? `Add RAM module to reach 16 GB total (£30-60).` : '',
  });
}

function checkRAM006(data: any): CheckResult {
  const type = safeGet(data, 'ram.slot_details[0].type', 'Unknown');
  return makeCheck('RAM-006', 'RAM', 'RAM Type', {
    status: type === 'Unknown' ? 'not_detected' : 'pass',
    value: type,
    evidence: `RAM type: ${type}`,
    explanation: `Your RAM is ${type}.`,
  });
}

function checkRAM007(data: any): CheckResult {
  const peak = safeGet<number>(data, 'ram.page_file_peak_mb', 0);
  const total = safeGet<number>(data, 'ram.total_gb', 0);
  const pressure = peak > 4000 && total > 0 && total <= 8;

  return makeCheck('RAM-007', 'RAM', 'Memory Pressure (Page File)', {
    status: pressure ? 'fail' : 'pass',
    severity: pressure ? 'high' : 'healthy',
    scoreImpact: pressure ? -15 : 0,
    value: peak,
    evidence: `Page file peaked at ${peak} MB`,
    explanation: pressure
      ? `Your PC has used ${(peak/1024).toFixed(1)} GB of slow disk-based memory because ${total} GB RAM isn't enough. This is the root cause of slowness.`
      : `Memory pressure is normal.`,
    recommendation: pressure ? `Upgrade to 16 GB RAM for permanent fix.` : '',
  });
}

function checkRAM008(data: any): CheckResult {
  const top = safeGet<any>(data, 'ram.top_processes[0]', null);
  const totalGb = safeGet<number>(data, 'ram.total_gb', 0);
  const topMb = top ? safeGet<number>(top, 'ram_mb', 0) : 0;
  const topPct = totalGb > 0 ? (topMb / (totalGb * 1024)) * 100 : 0;
  const dominant = topPct > 50;

  return makeCheck('RAM-008', 'RAM', 'Top RAM Consumer', {
    status: !top ? 'not_detected' : dominant ? 'fail' : 'pass',
    severity: dominant ? 'high' : 'healthy',
    scoreImpact: dominant ? -15 : 0,
    value: top ? `${top.name} (${topMb} MB)` : null,
    evidence: top ? `${top.name} using ${topMb} MB (${topPct.toFixed(0)}% of RAM)` : '',
    explanation: dominant
      ? `${top.name} is using ${topPct.toFixed(0)}% of your RAM (${topMb} MB).`
      : top
        ? `Top consumer: ${top.name} (${topMb} MB) — within normal range.`
        : '',
    recommendation: dominant ? `Close ${top.name} when not needed.` : '',
  });
}

// ─── STORAGE (10 checks) ───────────────────────────────────────────

function checkSTO001(data: any): CheckResult {
  const mainDrive = safeGet(data, 'storage.mainDrive', null);
  const pct = mainDrive ? safeGet<number>(mainDrive, 'used_pct', 0) || Math.round(((mainDrive.sizeGB - mainDrive.freeGB) / mainDrive.sizeGB) * 100) : safeGet<number>(data, 'storage.volumes[0].used_pct', 0);
  const free = mainDrive ? safeGet<number>(mainDrive, 'free_gb', 0) : safeGet<number>(data, 'storage.volumes[0].free_gb', 0);
  let severity: Severity = 'healthy';
  if (pct > 95) severity = 'critical';
  else if (pct > 85) severity = 'high';
  else if (pct > 75) severity = 'medium';

  return makeCheck('STO-001', 'Storage', 'C: Drive Free Space', {
    status: (pct === 0 && free === 0) ? 'not_detected' : pct > 75 ? (pct > 95 ? 'fail' : 'warning') : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: `${pct}% used`,
    evidence: `C: drive ${pct}% full, ${free} GB free`,
    explanation: pct > 85
      ? `C: drive critically full at ${pct}% (${free} GB free). Windows needs space to function.`
      : `Drive usage normal (${pct}%).`,
    recommendation: pct > 85 ? `Empty Recycle Bin, clear temp files, uninstall unused apps.` : '',
    tier: 'free',
  });
}

function checkSTO002(data: any): CheckResult {
  const total = safeGet<number>(data, 'storage.volumes[0].total_gb', 0);
  return makeCheck('STO-002', 'Storage', 'C: Drive Size', {
    status: total === 0 ? 'not_detected' : 'pass',
    value: total,
    evidence: `${total} GB total`,
    explanation: `C: drive capacity: ${total} GB`,
  });
}

function checkSTO003(data: any): CheckResult {
  const type = safeGet(data, 'storage.physical_disks[0].type', 'Unknown');
  const isHdd = type === 'HDD';
  return makeCheck('STO-003', 'Storage', 'Storage Type', {
    status: type === 'Unknown' ? 'not_detected' : isHdd ? 'warning' : 'pass',
    severity: isHdd ? 'medium' : 'healthy',
    scoreImpact: isHdd ? -8 : 0,
    value: type,
    evidence: `Storage: ${type}`,
    explanation: isHdd
      ? 'Your primary drive is an HDD (mechanical). SSD is 5-10x faster for boot and apps.'
      : `${type} detected — good performance.`,
    recommendation: isHdd ? `Upgrading to SSD (£40-100) is the single biggest speed upgrade.` : '',
  });
}

function checkSTO004(data: any): CheckResult {
  const health = safeGet(data, 'storage.physical_disks[0].health', 'Unknown');
  let severity: Severity = 'healthy';
  let status: CheckResult['status'] = 'pass';
  if (health === 'Unhealthy') { severity = 'critical'; status = 'fail'; }
  else if (health === 'Warning') { severity = 'high'; status = 'fail'; }

  return makeCheck('STO-004', 'Storage', 'Drive Health (SMART)', {
    status: health === 'Unknown' ? 'not_detected' : status,
    severity,
    scoreImpact: severityToScore(severity),
    value: health,
    evidence: `SMART status: ${health}`,
    explanation: health !== 'Healthy' && health !== 'Unknown'
      ? `URGENT: Drive reports ${health}. Back up data immediately and plan replacement.`
      : 'Drive health is good.',
    recommendation: health !== 'Healthy' && health !== 'Unknown' ? `Back up data NOW. Replace drive within 30 days.` : '',
  });
}

function checkSTO005(data: any): CheckResult {
  const trim = safeGet(data, 'storage.trim', 'Unknown');
  const type = safeGet(data, 'storage.physical_disks[0].type', 'Unknown');
  const issue = type === 'SSD' && trim === 'Disabled';

  return makeCheck('STO-005', 'Storage', 'SSD TRIM Status', {
    status: trim === 'Unknown' ? 'not_detected' : issue ? 'warning' : 'pass',
    severity: issue ? 'medium' : 'healthy',
    scoreImpact: issue ? -8 : 0,
    value: trim,
    evidence: `TRIM: ${trim}`,
    explanation: issue
      ? 'TRIM is disabled on your SSD. This shortens SSD lifespan.'
      : `TRIM: ${trim}`,
    recommendation: issue ? `Enable: Open admin command prompt, run "fsutil behavior set DisableDeleteNotify 0"` : '',
  });
}

function checkSTO006(data: any): CheckResult {
  const errs = safeGet<number>(data, 'storage.disk_error_count', 0);
  let severity: Severity = 'healthy';
  if (errs > 10) severity = 'critical';
  else if (errs > 3) severity = 'high';
  else if (errs > 0) severity = 'medium';

  return makeCheck('STO-006', 'Storage', 'Disk Errors (30 days)', {
    status: errs === 0 ? 'pass' : errs > 10 ? 'fail' : 'warning',
    severity,
    scoreImpact: severityToScore(severity),
    value: errs,
    evidence: `${errs} disk-related events in past 30 days`,
    explanation: errs > 0
      ? `${errs} disk errors logged. May indicate failing drive or bad cable.`
      : 'No disk errors detected.',
  });
}

function checkSTO007(data: any): CheckResult {
  const bl = safeGet(data, 'storage.bitlocker', 'Unknown');
  return makeCheck('STO-007', 'Storage', 'BitLocker Encryption', {
    status: bl === 'Unknown' ? 'not_detected' : 'pass',
    value: typeof bl === 'string' ? bl : 'Configured',
    evidence: `BitLocker: ${typeof bl === 'string' ? bl : 'Configured'}`,
    explanation: `Disk encryption status checked.`,
  });
}

function checkSTO008(data: any): CheckResult {
  const tmp = safeGet<number>(data, 'storage.temp_size_gb', 0);
  let severity: Severity = 'healthy';
  if (tmp > 5) severity = 'medium';
  else if (tmp > 2) severity = 'low';

  return makeCheck('STO-008', 'Storage', 'Temporary Files', {
    status: tmp > 2 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: tmp,
    evidence: `${tmp} GB in temp files`,
    explanation: tmp > 2
      ? `${tmp} GB of temporary files. Safe to delete.`
      : 'Temp file size normal.',
    recommendation: tmp > 2 ? `Run Disk Cleanup or Storage Sense to clear.` : '',
  });
}

function checkSTO009(data: any): CheckResult {
  const bin = safeGet<number>(data, 'storage.recycle_bin_gb', 0);
  let severity: Severity = 'healthy';
  if (bin > 5) severity = 'medium';
  else if (bin > 2) severity = 'low';

  return makeCheck('STO-009', 'Storage', 'Recycle Bin Size', {
    status: bin > 2 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: bin,
    evidence: `${bin} GB in Recycle Bin`,
    explanation: bin > 2 ? `${bin} GB in Recycle Bin. Empty to reclaim.` : 'Recycle Bin size normal.',
    recommendation: bin > 2 ? `Right-click Recycle Bin > Empty Recycle Bin` : '',
  });
}

function checkSTO010(data: any): CheckResult {
  const vols = safeGet<any[]>(data, 'storage.volumes', []);
  return makeCheck('STO-010', 'Storage', 'Drive Count', {
    status: vols.length === 0 ? 'not_detected' : 'pass',
    value: vols.length,
    evidence: `${vols.length} drives detected`,
    explanation: `${vols.length} drive(s) in this system.`,
  });
}

// ─── STARTUP (6 checks) ───────────────────────────────────────────

function checkSTA001(data: any): CheckResult {
  const startup = safeGet(data, 'startup', null);
  const count = startup ? (startup.program_count || (startup.items ? startup.items.length : 0)) : 0;
  let severity: Severity = 'healthy';
  if (count > 20) severity = 'high';
  else if (count > 12) severity = 'medium';
  else if (count > 8) severity = 'low';

  return makeCheck('STA-001', 'Startup', 'Startup Programs Count', {
    status: count === 0 ? 'not_detected' : count > 8 ? (count > 20 ? 'fail' : 'warning') : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} programs at startup`,
    explanation: count > 12
      ? `${count} programs start with Windows. Each adds boot time and uses RAM.`
      : `${count} startup programs — manageable.`,
    recommendation: count > 12 ? `Disable in Task Manager > Startup apps tab.` : '',
    tier: 'free',
  });
}

function checkSTA002(data: any): CheckResult {
  const time = safeGet<number>(data, 'startup.boot_time_seconds', 0);
  let severity: Severity = 'healthy';
  if (time > 90) severity = 'high';
  else if (time > 60) severity = 'medium';
  else if (time > 40) severity = 'low';

  return makeCheck('STA-002', 'Startup', 'Boot Time', {
    status: time === 0 ? 'not_detected' : time > 40 ? (time > 90 ? 'fail' : 'warning') : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: time,
    evidence: `Boot time: ${time} seconds`,
    explanation: time > 60
      ? `Boot time is ${time}s. Healthy PCs boot in 20-40s.`
      : `Boot time normal (${time}s).`,
  });
}

function checkSTA003(data: any): CheckResult {
  const progs = safeGet<any[]>(data, 'startup.programs', []);
  const heavyApps = ['Spotify','Discord','Steam','Adobe','Teams','Skype','iTunes','Razer','NVIDIA'];
  const found = progs.filter((p: any) => p && p.name && heavyApps.some((h: any) => p.name.includes(h)));
  let severity: Severity = 'healthy';
  if (found.length > 4) severity = 'high';
  else if (found.length > 2) severity = 'medium';

  return makeCheck('STA-003', 'Startup', 'High-Impact Startup Apps', {
    status: found.length === 0 ? 'pass' : found.length > 4 ? 'fail' : 'warning',
    severity,
    scoreImpact: severityToScore(severity),
    value: found.length,
    evidence: `${found.length} heavy apps: ${found.map((f: any) => f.name).join(', ')}`,
    explanation: found.length > 0
      ? `${found.length} resource-heavy apps starting with Windows.`
      : 'No heavy startup apps detected.',
    recommendation: found.length > 0 ? `These apps work fine when launched manually. Disable auto-start.` : '',
  });
}

function checkSTA004(data: any): CheckResult {
  const count = safeGet<number>(data, 'startup.non_ms_service_count', 0);
  let severity: Severity = 'healthy';
  if (count > 10) severity = 'medium';
  else if (count > 5) severity = 'low';

  return makeCheck('STA-004', 'Startup', 'Third-Party Auto-Start Services', {
    status: count === 0 ? 'pass' : count > 5 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} third-party services`,
    explanation: `${count} non-Microsoft services start automatically.`,
  });
}

function checkSTA005(data: any): CheckResult {
  const progs = safeGet<any[]>(data, 'startup.programs', []);
  return makeCheck('STA-005', 'Startup', 'Startup Sources', {
    status: progs.length === 0 ? 'not_detected' : 'pass',
    value: progs.length,
    evidence: `Startup items registered in ${new Set(progs.map((p: any) => p?.location)).size} locations`,
    explanation: 'Startup items audit complete.',
  });
}

function checkSTA006(data: any): CheckResult {
  const tasks = safeGet<number>(data, 'startup.scheduled_tasks_count', 0);
  let severity: Severity = 'healthy';
  if (tasks > 15) severity = 'medium';
  else if (tasks > 8) severity = 'low';

  return makeCheck('STA-006', 'Startup', 'Scheduled Tasks at Logon', {
    status: tasks === 0 ? 'not_detected' : tasks > 8 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: tasks,
    evidence: `${tasks} login-triggered scheduled tasks`,
    explanation: `${tasks} scheduled tasks run at login.`,
  });
}

// ─── BATTERY (5 checks) ───────────────────────────────────────────

function checkBAT001(data: any): CheckResult {
  const present = safeGet<boolean>(data, 'battery.present', false);
  return makeCheck('BAT-001', 'Battery', 'Battery Detection', {
    status: 'pass',
    value: present ? 'Present' : 'Not present',
    evidence: present ? 'Battery detected' : 'No battery (desktop PC)',
    explanation: present ? 'Battery present.' : 'Desktop PC — no battery checks apply.',
  });
}

function checkBAT002(data: any): CheckResult {
  const wear = safeGet<number>(data, 'battery.wear_pct', 0);
  const present = safeGet<boolean>(data, 'battery.present', false);
  let severity: Severity = 'healthy';
  if (wear > 70) severity = 'critical';
  else if (wear > 50) severity = 'high';
  else if (wear > 30) severity = 'medium';
  else if (wear > 15) severity = 'low';

  return makeCheck('BAT-002', 'Battery', 'Battery Wear', {
    status: !present ? 'not_detected' : wear > 15 ? (wear > 50 ? 'fail' : 'warning') : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: wear,
    evidence: present ? `${wear}% wear` : 'N/A',
    explanation: wear > 30
      ? `Battery has lost ${wear}% of original capacity. Runtime is reduced.`
      : present ? `Battery wear acceptable (${wear}%).` : '',
    recommendation: wear > 50 ? `Replace battery (£60-120 from manufacturer).` : '',
  });
}

function checkBAT003(data: any): CheckResult {
  const charge = safeGet<number>(data, 'battery.charge_pct', 0);
  const present = safeGet<boolean>(data, 'battery.present', false);
  return makeCheck('BAT-003', 'Battery', 'Current Charge', {
    status: !present ? 'not_detected' : 'pass',
    value: charge,
    evidence: present ? `${charge}% charge` : 'N/A',
    explanation: present ? `Battery at ${charge}%.` : '',
  });
}

function checkBAT004(data: any): CheckResult {
  const status = safeGet(data, 'battery.status', 'Unknown');
  const present = safeGet<boolean>(data, 'battery.present', false);
  return makeCheck('BAT-004', 'Battery', 'Charge Status', {
    status: !present ? 'not_detected' : 'pass',
    value: status,
    evidence: present ? `Status: ${status}` : 'N/A',
    explanation: present ? `Battery is ${status.toLowerCase()}.` : '',
  });
}

function checkBAT005(data: any): CheckResult {
  const plan = safeGet(data, 'battery.power_plan', 'Unknown');
  const present = safeGet<boolean>(data, 'battery.present', false);
  const mismatched = present && plan.includes('High Performance');
  return makeCheck('BAT-005', 'Battery', 'Power Plan', {
    status: plan === 'Unknown' ? 'not_detected' : mismatched ? 'warning' : 'pass',
    severity: mismatched ? 'low' : 'healthy',
    scoreImpact: mismatched ? -3 : 0,
    value: plan,
    evidence: `Active plan: ${plan}`,
    explanation: mismatched
      ? `"High Performance" plan drains battery faster on laptops.`
      : `Power plan: ${plan}`,
    recommendation: mismatched ? `Switch to Balanced: Settings > Power options.` : '',
  });
}

// ─── NETWORK/WIFI (8 checks) ───────────────────────────────────────────

function checkNET001(data: any): CheckResult {
  const adapter = safeGet(data, 'network.adapter', 'Unknown');
  return makeCheck('NET-001', 'Network/WiFi', 'Active Network Adapter', {
    status: adapter === 'Unknown' || adapter === 'None' ? 'not_detected' : 'pass',
    value: adapter,
    evidence: `Active: ${adapter}`,
    explanation: `Active network adapter: ${adapter}`,
  });
}

function checkNET002(data: any): CheckResult {
  const signal = safeGet<number>(data, 'network.wifi.signal_pct', 0);
  let severity: Severity = 'healthy';
  if (signal > 0 && signal < 30) severity = 'high';
  else if (signal > 0 && signal < 50) severity = 'medium';
  else if (signal > 0 && signal < 70) severity = 'low';

  return makeCheck('NET-002', 'Network/WiFi', 'WiFi Signal Strength', {
    status: signal === 0 ? 'not_detected' : signal < 50 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: signal,
    evidence: `Signal: ${signal}%`,
    explanation: signal < 50
      ? `WiFi signal weak (${signal}%). Move closer to router.`
      : `WiFi signal: ${signal}%`,
  });
}

function checkNET003(data: any): CheckResult {
  const speed = safeGet<number>(data, 'network.wifi.link_speed_mbps', 0);
  return makeCheck('NET-003', 'Network/WiFi', 'WiFi Link Speed', {
    status: speed === 0 ? 'not_detected' : 'pass',
    value: speed,
    evidence: `Link speed: ${speed} Mbps`,
    explanation: `WiFi connected at ${speed} Mbps.`,
  });
}

function checkNET004(data: any): CheckResult {
  const band = safeGet(data, 'network.wifi.band', 'Unknown');
  const signal = safeGet<number>(data, 'network.wifi.signal_pct', 0);
  const suggest = band.includes('2.4') && signal > 60;
  return makeCheck('NET-004', 'Network/WiFi', 'WiFi Band', {
    status: band === 'Unknown' ? 'not_detected' : suggest ? 'warning' : 'pass',
    severity: suggest ? 'low' : 'healthy',
    scoreImpact: suggest ? -3 : 0,
    value: band,
    evidence: `Band: ${band}`,
    explanation: suggest
      ? `On 2.4 GHz. 5 GHz is faster with less interference.`
      : `WiFi band: ${band}`,
  });
}

function checkNET005(data: any): CheckResult {
  const disc = safeGet<number>(data, 'network.disconnects_7d', 0);
  let severity: Severity = 'healthy';
  if (disc > 20) severity = 'high';
  else if (disc > 10) severity = 'medium';
  else if (disc > 5) severity = 'low';

  return makeCheck('NET-005', 'Network/WiFi', 'WiFi Disconnects', {
    status: disc === 0 ? 'pass' : disc > 5 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: disc,
    evidence: `${disc} disconnects in 7 days`,
    explanation: disc > 10
      ? `WiFi disconnected ${disc} times in past week. Pattern of instability.`
      : `WiFi stable (${disc} disconnects).`,
  });
}

function checkNET006(data: any): CheckResult {
  const date = safeGet(data, 'network.wifi_driver.driver_date', '');
  let isOld = false;
  if (date) {
    const driverDate = new Date(date);
    const yearsOld = (Date.now() - driverDate.getTime()) / (365 * 24 * 60 * 60 * 1000);
    isOld = yearsOld > 2;
  }
  return makeCheck('NET-006', 'Network/WiFi', 'WiFi Driver Age', {
    status: !date ? 'not_detected' : isOld ? 'warning' : 'pass',
    severity: isOld ? 'medium' : 'healthy',
    scoreImpact: isOld ? -8 : 0,
    value: date,
    evidence: `WiFi driver date: ${date || 'Unknown'}`,
    explanation: isOld ? `WiFi driver is 2+ years old.` : `WiFi driver is current.`,
  });
}

function checkNET007(data: any): CheckResult {
  const reach = safeGet<boolean>(data, 'network.gateway_reachable', false);
  return makeCheck('NET-007', 'Network/WiFi', 'Gateway Connectivity', {
    status: reach ? 'pass' : 'fail',
    severity: reach ? 'healthy' : 'high',
    scoreImpact: reach ? 0 : -15,
    value: reach ? 'Reachable' : 'Unreachable',
    evidence: reach ? 'Router gateway responding' : 'Cannot reach router',
    explanation: reach ? 'Network connectivity OK.' : 'Cannot reach your router.',
  });
}

function checkNET008(data: any): CheckResult {
  const dns = safeGet<any[]>(data, 'network.dns_servers', []);
  return makeCheck('NET-008', 'Network/WiFi', 'DNS Servers', {
    status: dns.length === 0 ? 'not_detected' : 'pass',
    value: dns.length,
    evidence: `${dns.length} DNS servers configured`,
    explanation: `DNS servers in use: ${dns.join(', ') || 'none configured'}`,
  });
}

// ─── WINDOWS HEALTH (8 checks) ───────────────────────────────────────────

function checkWIN001(data: any): CheckResult {
  const date = safeGet(data, 'windows_health.last_update_installed', '');
  let daysAgo = 999;
  if (date) {
    daysAgo = Math.floor((Date.now() - new Date(date).getTime()) / (24 * 60 * 60 * 1000));
  }
  let severity: Severity = 'healthy';
  if (daysAgo > 90) severity = 'high';
  else if (daysAgo > 30) severity = 'medium';

  return makeCheck('WIN-001', 'Windows Health', 'Last Windows Update', {
    status: !date ? 'not_detected' : daysAgo > 30 ? (daysAgo > 90 ? 'fail' : 'warning') : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: date,
    evidence: `Last update: ${date || 'Unknown'} (${daysAgo} days ago)`,
    explanation: daysAgo > 30
      ? `Last update was ${daysAgo} days ago. Updates contain security and stability fixes.`
      : `Updates current (${daysAgo} days ago).`,
    recommendation: daysAgo > 30 ? `Settings > Windows Update > Check for updates.` : '',
  });
}

function checkWIN002(data: any): CheckResult {
  const pending = safeGet<boolean>(data, 'windows_health.pending_reboot', false);
  return makeCheck('WIN-002', 'Windows Health', 'Pending Reboot', {
    status: pending ? 'warning' : 'pass',
    severity: pending ? 'medium' : 'healthy',
    scoreImpact: pending ? -8 : 0,
    value: pending ? 'Required' : 'None',
    evidence: pending ? 'Reboot pending' : 'No pending reboots',
    explanation: pending ? 'A reboot is pending. Save work and restart.' : 'No pending reboot.',
  });
}

function checkWIN003(data: any): CheckResult {
  const status = safeGet(data, 'windows_health.activation', 'Unknown');
  const notActive = status && !status.toLowerCase().includes('activated');
  return makeCheck('WIN-003', 'Windows Health', 'Windows Activation', {
    status: status === 'Unknown' ? 'not_detected' : notActive ? 'fail' : 'pass',
    severity: notActive ? 'high' : 'healthy',
    scoreImpact: notActive ? -15 : 0,
    value: status,
    evidence: `Activation: ${status}`,
    explanation: notActive ? 'Windows is not activated.' : 'Windows activated.',
  });
}

function checkWIN004(data: any): CheckResult {
  const rawCount = safeGet<number>(data, 'windows_health.antivirus_count', -1);
  const defenderRealtime = safeGet<boolean>(data, 'security.defender_realtime', false);
  const defenderEnabled = safeGet<boolean>(data, 'security.defender_enabled', false);
  const defenderActive = defenderRealtime || defenderEnabled;

  // When the scanner can't enumerate AV providers, treat that as "unknown",
  // not "zero installed". Defender status (collected separately) is the real
  // signal here.
  const count = rawCount < 0 ? (defenderActive ? 1 : 0) : rawCount;

  let severity: Severity = 'healthy';
  let status: CheckResult['status'] = 'pass';
  let evidence = `${count} antivirus product(s) active`;
  let explanation = 'One antivirus active — correct configuration.';
  let recommendation = '';

  if (count === 0) {
    if (defenderRealtime) {
      // Defender is on but no third-party AV — perfectly fine.
      severity = 'healthy';
      status = 'pass';
      evidence = 'Windows Defender real-time protection is active.';
      explanation = 'Windows Defender is providing real-time protection. No third-party antivirus is required.';
    } else if (defenderActive) {
      // Defender is registered but real-time is off — warn, don't critical.
      severity = 'medium';
      status = 'warning';
      evidence = 'Windows Defender present but real-time protection is OFF.';
      explanation = 'Windows Defender is installed but its real-time protection is disabled. Turn it on to block malware as it arrives.';
      recommendation = 'Open Windows Security > Virus & threat protection > Manage settings > turn ON Real-time protection.';
    } else {
      // No defender info AND no third-party AV — this is the real critical case.
      severity = 'critical';
      status = 'fail';
      evidence = 'No active real-time protection detected.';
      explanation = 'No active antivirus or Windows Defender real-time protection was detected. Your PC is unprotected against malware.';
      recommendation = 'Open Windows Security > Virus & threat protection > Manage settings > turn ON Real-time protection. Do not install third-party "free antivirus" advertised in pop-ups.';
    }
  } else if (count === 1) {
    severity = 'healthy';
    status = 'pass';
    evidence = '1 antivirus product active.';
    explanation = 'Exactly one antivirus is providing protection — the correct configuration.';
  } else {
    // count > 1
    severity = 'high';
    status = 'warning';
    evidence = `${count} antivirus products installed simultaneously.`;
    explanation = `${count} antivirus programs are installed at the same time. They conflict with each other, slow the PC, and can leave gaps in protection.`;
    recommendation = 'Keep ONE antivirus (Windows Defender is enough for most users). Uninstall the others from Settings > Apps > Installed apps, then restart.';
  }

  return makeCheck('WIN-004', 'Windows Health', 'Antivirus Products', {
    status,
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence,
    explanation,
    recommendation,
  });
}

function checkWIN005(data: any): CheckResult {
  const enabled = safeGet<boolean>(data, 'windows_health.firewall_enabled', false);
  return makeCheck('WIN-005', 'Windows Health', 'Windows Firewall', {
    status: enabled ? 'pass' : 'fail',
    severity: enabled ? 'healthy' : 'high',
    scoreImpact: enabled ? 0 : -15,
    value: enabled ? 'Enabled' : 'Disabled',
    evidence: `Firewall: ${enabled ? 'Enabled' : 'Disabled'}`,
    explanation: enabled ? 'Firewall protecting your PC.' : 'Firewall disabled — security risk.',
  });
}

function checkWIN006(data: any): CheckResult {
  const sfc = safeGet(data, 'windows_health.sfc_status', 'Unknown');
  const corrupt = sfc === 'Issues Found';
  return makeCheck('WIN-006', 'Windows Health', 'System File Integrity', {
    status: sfc === 'Unknown' ? 'not_detected' : corrupt ? 'warning' : 'pass',
    severity: corrupt ? 'medium' : 'healthy',
    scoreImpact: corrupt ? -8 : 0,
    value: sfc,
    evidence: `SFC last result: ${sfc}`,
    explanation: corrupt ? 'Windows file corruption detected.' : 'System files OK.',
    recommendation: corrupt ? `Run sfc /scannow in admin command prompt.` : '',
  });
}

function checkWIN007(data: any): CheckResult {
  const age = safeGet<number>(data, 'windows_health.defender_signature_age_days', 0);
  let severity: Severity = 'healthy';
  if (age > 14) severity = 'high';
  else if (age > 7) severity = 'medium';

  return makeCheck('WIN-007', 'Windows Health', 'Antivirus Definitions', {
    status: age > 7 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: age,
    evidence: `${age} days since last update`,
    explanation: age > 7
      ? `Antivirus definitions are ${age} days old.`
      : 'Antivirus definitions current.',
  });
}

function checkWIN008(data: any): CheckResult {
  // Indirect indicator via update install recency
  return makeCheck('WIN-008', 'Windows Health', 'Windows Update Service', {
    status: 'pass',
    value: 'Running',
    evidence: 'Update service operational',
    explanation: 'Windows Update service is operational.',
  });
}

// ─── EVENT VIEWER (5 checks) ───────────────────────────────────────────

function checkEVT001(data: any): CheckResult {
  const errs = safeGet<number>(data, 'stability.critical_errors_30d', 0);
  let severity: Severity = 'healthy';
  if (errs > 20) severity = 'high';
  else if (errs > 5) severity = 'medium';

  return makeCheck('EVT-001', 'Event Viewer', 'Critical System Errors', {
    status: errs > 5 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: errs,
    evidence: `${errs} critical errors in past 30 days`,
    explanation: errs > 5 ? `${errs} critical errors in 30 days.` : 'Minimal critical errors.',
  });
}

function checkEVT002(data: any): CheckResult {
  const errs = safeGet<number>(data, 'stability.error_events_30d', 0);
  let severity: Severity = 'healthy';
  if (errs > 100) severity = 'high';
  else if (errs > 50) severity = 'medium';

  return makeCheck('EVT-002', 'Event Viewer', 'Error-Level Events', {
    status: errs > 50 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: errs,
    evidence: `${errs} error events`,
    explanation: errs > 50 ? `${errs} error events suggest underlying issues.` : 'Error rate acceptable.',
  });
}

function checkEVT003(data: any): CheckResult {
  const crashes = safeGet<number>(data, 'stability.service_crashes_30d', 0);
  return makeCheck('EVT-003', 'Event Viewer', 'Service Crashes', {
    status: crashes > 10 ? 'warning' : 'pass',
    severity: crashes > 10 ? 'medium' : 'healthy',
    scoreImpact: crashes > 10 ? -8 : 0,
    value: crashes,
    evidence: `${crashes} service crashes`,
    explanation: crashes > 10 ? `${crashes} services crashed recently.` : 'Services stable.',
  });
}

function checkEVT004(data: any): CheckResult {
  const installs = safeGet<number>(data, 'stability.recent_installs_14d', 0);
  return makeCheck('EVT-004', 'Event Viewer', 'Recent Software Changes', {
    status: 'pass',
    value: installs,
    evidence: `${installs} installs/uninstalls in 14 days`,
    explanation: `${installs} software changes recently. Used for correlation analysis.`,
  });
}

function checkEVT005(data: any): CheckResult {
  const trend = safeGet<any>(data, 'stability.reliability_trend', null);
  const declining = trend && trend.decline && trend.decline > 1.0;
  return makeCheck('EVT-005', 'Event Viewer', 'PC Stability Trend', {
    status: !trend ? 'not_detected' : declining ? 'fail' : 'pass',
    severity: declining ? 'high' : 'healthy',
    scoreImpact: declining ? -15 : 0,
    value: trend?.current || null,
    evidence: trend ? `Index changed: ${trend.old} → ${trend.current}` : 'No trend data',
    explanation: declining
      ? `PC stability declining from ${trend.old} to ${trend.current}.`
      : 'PC stability stable.',
  });
}

// ─── BSOD/CRASHES (4 checks) ───────────────────────────────────────────

function checkBSD001(data: any): CheckResult {
  const count = safeGet<number>(data, 'stability.bsod_count_90d', 0);
  let severity: Severity = 'healthy';
  if (count > 4) severity = 'critical';
  else if (count > 2) severity = 'high';
  else if (count > 0) severity = 'medium';

  return makeCheck('BSD-001', 'BSOD/Crashes', 'BSOD Frequency', {
    status: count === 0 ? 'pass' : count > 4 ? 'fail' : 'warning',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} blue screen crashes in 90 days`,
    explanation: count > 0
      ? `PC crashed (BSOD) ${count} times in past 90 days.`
      : 'No BSODs detected.',
    recommendation: count > 2 ? `Update drivers, run memory test, check for overheating.` : '',
  });
}

function checkBSD002(data: any): CheckResult {
  const count = safeGet<number>(data, 'stability.kernel_power_41_count', 0);
  let severity: Severity = 'healthy';
  if (count > 3) severity = 'high';
  else if (count > 0) severity = 'medium';

  return makeCheck('BSD-002', 'BSOD/Crashes', 'Unexpected Shutdowns', {
    status: count === 0 ? 'pass' : count > 3 ? 'fail' : 'warning',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} unexpected shutdowns in 30 days`,
    explanation: count > 0
      ? `${count} unexpected shutdowns. May indicate power supply or thermal issue.`
      : 'No unexpected shutdowns.',
  });
}

function checkBSD003(data: any): CheckResult {
  const count = safeGet<number>(data, 'stability.app_crash_count_30d', 0);
  let severity: Severity = 'healthy';
  if (count > 50) severity = 'high';
  else if (count > 20) severity = 'medium';

  return makeCheck('BSD-003', 'BSOD/Crashes', 'Application Crashes', {
    status: count > 20 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} app crashes in 30 days`,
    explanation: count > 20 ? `${count} application crashes recently.` : 'Apps stable.',
  });
}

function checkBSD004(data: any): CheckResult {
  const count = safeGet<number>(data, 'stability.minidump_count', 0);
  let severity: Severity = 'healthy';
  if (count > 5) severity = 'high';
  else if (count > 0) severity = 'medium';

  return makeCheck('BSD-004', 'BSOD/Crashes', 'Crash Dump Files', {
    status: count === 0 ? 'pass' : 'warning',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} minidump files`,
    explanation: count > 0 ? `${count} crash dumps indicate past BSODs.` : 'No crash dumps.',
  });
}

// ─── DRIVERS (5 checks) ───────────────────────────────────────────

function checkDRV001(data: any): CheckResult {
  const count = safeGet<number>(data, 'drivers.problem_count', 0);
  let severity: Severity = 'healthy';
  if (count > 3) severity = 'high';
  else if (count > 0) severity = 'medium';

  return makeCheck('DRV-001', 'Drivers', 'Problem Devices', {
    status: count === 0 ? 'pass' : count > 3 ? 'fail' : 'warning',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} devices with driver problems`,
    explanation: count > 0 ? `${count} devices have driver issues.` : 'All devices working.',
  });
}

function checkDRV002(data: any): CheckResult {
  const count = safeGet<number>(data, 'drivers.outdated_count', 0);
  let severity: Severity = 'healthy';
  if (count > 5) severity = 'medium';
  else if (count > 2) severity = 'low';

  return makeCheck('DRV-002', 'Drivers', 'Outdated Drivers', {
    status: count > 2 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: count,
    evidence: `${count} drivers 2+ years old`,
    explanation: `${count} drivers haven't been updated in 2+ years.`,
  });
}

function checkDRV003(data: any): CheckResult {
  const date = safeGet(data, 'display.gpu_driver_date', '');
  let isOld = false, severity: Severity = 'healthy';
  if (date) {
    const years = (Date.now() - new Date(date).getTime()) / (365 * 24 * 60 * 60 * 1000);
    if (years > 2) { isOld = true; severity = 'medium'; }
    else if (years > 1) { severity = 'low'; }
  }
  return makeCheck('DRV-003', 'Drivers', 'GPU Driver', {
    status: !date ? 'not_detected' : isOld ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: date,
    evidence: `GPU driver date: ${date}`,
    explanation: isOld ? `GPU driver is 2+ years old.` : `GPU driver date: ${date}`,
  });
}

function checkDRV004(data: any): CheckResult {
  const count = safeGet<number>(data, 'drivers.usb_count', 0);
  return makeCheck('DRV-004', 'Drivers', 'USB Devices', {
    status: 'pass',
    value: count,
    evidence: `${count} USB devices`,
    explanation: `${count} USB devices connected.`,
  });
}

function checkDRV005(data: any): CheckResult {
  const count = safeGet<number>(data, 'drivers.unsigned_count', 0);
  return makeCheck('DRV-005', 'Drivers', 'Unsigned Drivers', {
    status: count === 0 ? 'pass' : 'warning',
    severity: count > 0 ? 'medium' : 'healthy',
    scoreImpact: count > 0 ? -8 : 0,
    value: count,
    evidence: `${count} unsigned drivers`,
    explanation: count > 0 ? `${count} unsigned drivers detected.` : 'All drivers signed.',
  });
}

// ─── SECURITY (5 checks) ───────────────────────────────────────────

function checkSEC001(data: any): CheckResult {
  const enabled = safeGet<any>(data, 'security.defender_realtime', 'Unknown');
  const off = enabled === false || enabled === 'Disabled';
  return makeCheck('SEC-001', 'Security', 'Real-Time Protection', {
    status: enabled === 'Unknown' ? 'not_detected' : off ? 'fail' : 'pass',
    severity: off ? 'high' : 'healthy',
    scoreImpact: off ? -15 : 0,
    value: enabled,
    evidence: `Real-time protection: ${enabled}`,
    explanation: off ? 'Real-time protection is OFF. Major security risk.' : 'Real-time protection active.',
  });
}

function checkSEC002(data: any): CheckResult {
  const protected_ = safeGet<any>(data, 'security.tamper_protected', 'Unknown');
  const off = protected_ === false || protected_ === 'Disabled';
  return makeCheck('SEC-002', 'Security', 'Tamper Protection', {
    status: protected_ === 'Unknown' ? 'not_detected' : off ? 'warning' : 'pass',
    severity: off ? 'low' : 'healthy',
    scoreImpact: off ? -3 : 0,
    value: protected_,
    evidence: `Tamper protection: ${protected_}`,
    explanation: off ? 'Tamper protection off.' : 'Tamper protection active.',
  });
}

function checkSEC003(data: any): CheckResult {
  const days = safeGet<number>(data, 'security.last_scan_days_ago', 0);
  let severity: Severity = 'healthy';
  if (days > 14) severity = 'medium';
  else if (days > 7) severity = 'low';

  return makeCheck('SEC-003', 'Security', 'Last Antivirus Scan', {
    status: days > 7 ? 'warning' : 'pass',
    severity,
    scoreImpact: severityToScore(severity),
    value: days,
    evidence: `${days} days since last scan`,
    explanation: days > 7 ? `Last scan was ${days} days ago.` : 'Antivirus scans current.',
  });
}

function checkSEC004(data: any): CheckResult {
  const level = safeGet<any>(data, 'security.uac_level', 'Unknown');
  const off = level === 'Disabled' || level === '0';
  return makeCheck('SEC-004', 'Security', 'UAC (User Account Control)', {
    status: level === 'Unknown' ? 'not_detected' : off ? 'warning' : 'pass',
    severity: off ? 'medium' : 'healthy',
    scoreImpact: off ? -8 : 0,
    value: level,
    evidence: `UAC: ${level}`,
    explanation: off ? 'UAC disabled — security risk.' : `UAC level: ${level}`,
  });
}

function checkSEC005(data: any): CheckResult {
  const status = safeGet<any>(data, 'security.smartscreen', 'Unknown');
  const off = status === 'Off';
  return makeCheck('SEC-005', 'Security', 'SmartScreen Protection', {
    status: status === 'Unknown' ? 'not_detected' : off ? 'warning' : 'pass',
    severity: off ? 'low' : 'healthy',
    scoreImpact: off ? -3 : 0,
    value: status,
    evidence: `SmartScreen: ${status}`,
    explanation: off ? 'SmartScreen disabled.' : 'SmartScreen active.',
  });
}

// ─── PERFORMANCE BOTTLENECKS (2 checks) ───────────────────────────────────────────

function checkPRF001(data: any, allChecks: CheckResult[]): CheckResult {
  // Identify the category with most score damage
  const categoryDamage: Record<string, number> = {};
  for (const c of allChecks) {
    if (c.scoreImpact < 0) {
      categoryDamage[c.category] = (categoryDamage[c.category] || 0) + Math.abs(c.scoreImpact);
    }
  }
  const sorted = Object.entries(categoryDamage).sort((a, b) => b[1] - a[1]);
  const primary = sorted[0]?.[0] || 'None';

  return makeCheck('PRF-001', 'Performance Bottlenecks', 'Primary Bottleneck', {
    status: 'pass',
    value: primary,
    evidence: `Most-impacted category: ${primary}`,
    explanation: primary !== 'None'
      ? `Your primary bottleneck is ${primary}. Fixing this category will have the largest impact.`
      : 'No major bottlenecks detected.',
  });
}

function checkPRF002(data: any): CheckResult {
  const peak = safeGet<number>(data, 'ram.page_file_peak_mb', 0);
  const total = safeGet<number>(data, 'ram.total_gb', 0);
  const pressure = peak > 4000 && total > 0 && total <= 8;
  return makeCheck('PRF-002', 'Performance Bottlenecks', 'Memory Pressure Pattern', {
    status: pressure ? 'fail' : 'pass',
    severity: pressure ? 'high' : 'healthy',
    scoreImpact: pressure ? -15 : 0,
    value: pressure ? 'Chronic' : 'Normal',
    evidence: `Page file peak: ${peak} MB on ${total} GB system`,
    explanation: pressure
      ? `Chronic memory pressure detected. PC routinely runs out of RAM and uses slow disk storage.`
      : 'No chronic memory pressure.',
  });
}

// ============================================================================
// MAIN RUNNER
// ============================================================================

export function runDiagnosticEngine(parsedData: ParsedScanData): DiagnosticReport {
  const data = parsedData.data || parsedData;

  // Resilience Audit: Safe check execution loop
  const checksToRun = [
    // System Overview (8)
    checkSYS001, checkSYS002, checkSYS003, checkSYS004,
    checkSYS005, checkSYS006, checkSYS007, checkSYS008,
    // CPU (6)
    checkCPU001, checkCPU002, checkCPU003,
    checkCPU004, checkCPU005, checkCPU006,
    // RAM (8)
    checkRAM001, checkRAM002, checkRAM003, checkRAM004,
    checkRAM005, checkRAM006, checkRAM007, checkRAM008,
    // Storage (10)
    checkSTO001, checkSTO002, checkSTO003, checkSTO004,
    checkSTO005, checkSTO006, checkSTO007, checkSTO008,
    checkSTO009, checkSTO010,
    // Startup (6)
    checkSTA001, checkSTA002, checkSTA003,
    checkSTA004, checkSTA005, checkSTA006,
    // Battery (5)
    checkBAT001, checkBAT002, checkBAT003,
    checkBAT004, checkBAT005,
    // Network (8)
    checkNET001, checkNET002, checkNET003, checkNET004,
    checkNET005, checkNET006, checkNET007, checkNET008,
    // Windows Health (8)
    checkWIN001, checkWIN002, checkWIN003, checkWIN004,
    checkWIN005, checkWIN006, checkWIN007, checkWIN008,
    // Event Viewer (5)
    checkEVT001, checkEVT002, checkEVT003,
    checkEVT004, checkEVT005,
    // BSOD/Crashes (4)
    checkBSD001, checkBSD002, checkBSD003, checkBSD004,
    // Drivers (5)
    checkDRV001, checkDRV002, checkDRV003,
    checkDRV004, checkDRV005,
    // Security (5)
    checkSEC001, checkSEC002, checkSEC003,
    checkSEC004, checkSEC005,
  ];

  const checks: CheckResult[] = [];
  for (const fn of checksToRun) {
    try {
      checks.push(fn(data));
    } catch (e: any) {
      console.error(`[DiagnosticEngine] Check crashed: ${fn.name}`, e);
      // Push placeholder instead of crashing
      checks.push({
        id: 'ERR-999',
        category: 'Diagnostic Error',
        name: `Check Failure: ${fn.name}`,
        status: 'not_detected',
        severity: 'healthy',
        scoreImpact: 0,
        value: null,
        evidence: e.message || 'Check execution error',
        explanation: 'An internal error occurred while evaluating this system component.',
        recommendation: 'Please try running the scanner again.',
        tier: 'pro'
      });
    }
  }

  // Performance checks (need full check list)
  try {
    const prf1 = checkPRF001(data, checks);
    const prf2 = checkPRF002(data);
    checks.push(prf1, prf2);
  } catch (e) {
    console.error("[DiagnosticEngine] Performance checks failed", e);
  }

  // Build category scores
  const categoryMap: Record<string, CategoryScore> = {};
  for (const check of checks) {
    if (!categoryMap[check.category]) {
      categoryMap[check.category] = {
        category: check.category,
        score: 100,
        maxScore: 100,
        weight: CATEGORY_WEIGHTS[check.category] || 0,
        checks: [],
        passCount: 0,
        warningCount: 0,
        failCount: 0,
      };
    }
    const cat = categoryMap[check.category];
    cat.checks.push(check);
    cat.score = Math.max(0, cat.score + check.scoreImpact);
    if (check.status === 'pass') cat.passCount++;
    else if (check.status === 'warning') cat.warningCount++;
    else if (check.status === 'fail') cat.failCount++;
  }

  const categoryScores = Object.values(categoryMap);

  // Calculate weighted health score
  let weightedScore = 0;
  let totalWeight = 0;
  for (const cat of categoryScores) {
    if (cat.weight > 0) {
      weightedScore += cat.score * cat.weight;
      totalWeight += cat.weight;
    }
  }
  const healthScore = totalWeight > 0
    ? Math.round(weightedScore / totalWeight)
    : 100;

  // Determine score status
  let scoreStatus: DiagnosticReport['scoreStatus'] = 'excellent';
  if (healthScore < 40) scoreStatus = 'critical';
  else if (healthScore < 60) scoreStatus = 'poor';
  else if (healthScore < 75) scoreStatus = 'fair';
  else if (healthScore < 90) scoreStatus = 'good';

  // Counts
  const passCount = checks.filter(c => c.status === 'pass').length;
  const warningCount = checks.filter(c => c.status === 'warning').length;
  const criticalCount = checks.filter(c => c.status === 'fail').length;
  const notDetectedCount = checks.filter(c => c.status === 'not_detected').length;

  // Find primary bottleneck
  const bottleneck = checks.find(c => c.id === 'PRF-001');

  // Build findings
  const criticalFindings = checks.filter(c => c.status === 'fail');
  const warningFindings = checks.filter(c => c.status === 'warning');
  const passedChecks = checks.filter(c => c.status === 'pass');

  // Build fix plan from worst findings
  const allBadFindings = [...criticalFindings, ...warningFindings]
    .sort((a, b) => a.scoreImpact - b.scoreImpact);
  const fixPlan: FixStep[] = allBadFindings.slice(0, 5).map((f, i) => ({
    order: i + 1,
    title: f.name,
    timeMinutes: 5,
    difficulty: 'easy' as const,
    description: f.recommendation || f.explanation,
    steps: f.recommendation ? [f.recommendation] : [f.explanation],
    relatedCheckIds: [f.id],
  }));

  return {
    scanId: parsedData.scanId || 'unknown',
    scanTimestamp: parsedData.timestamp || new Date().toISOString(),
    totalChecks: 80,
    passCount,
    warningCount,
    criticalCount,
    notDetectedCount,
    healthScore,
    scoreStatus,
    primaryBottleneck: bottleneck?.value as string || 'None',
    bottleneckExplanation: bottleneck?.explanation || '',
    categoryScores,
    allChecks: checks,
    criticalFindings,
    warningFindings,
    passedChecks,
    fixPlan,
    manufacturer: safeGet(data, 'system.manufacturer', 'Unknown'),
    model: safeGet(data, 'system.model', 'Unknown'),
  };
}

// ============================================================================
// FIX GUIDANCE TEMPLATES (Pro report enhancement)
// ----------------------------------------------------------------------------
// Plain-English remediation playbooks attached to Pro findings. Keyed by
// check ID. Wording is aimed at non-technical users.
// ============================================================================

export type FixDifficulty = 'easy' | 'medium' | 'hard';

export interface FixGuidance {
  meaning: string;            // Plain-English meaning of the issue
  steps: string[];            // Numbered fix steps
  expectedResult: string;     // What the user should see after fixing
  avoid: string[];            // What NOT to do
  difficulty: FixDifficulty;
  timeMinutes: number;        // Estimated time
  technicianNeeded: boolean;  // Should they call a pro?
}

const G = {
  realtimeOff: {
    meaning:
      'Windows Defender real-time protection is OFF. Your PC cannot block malware as it arrives, which is the single biggest everyday risk to your files and passwords.',
    steps: [
      'Open the Start menu.',
      'Search for "Windows Security" and open it.',
      'Click "Virus & threat protection".',
      'Under "Virus & threat protection settings", click "Manage settings".',
      'Switch "Real-time protection" ON.',
      'If it turns itself OFF again, you probably have a second antivirus installed — uninstall it from Settings > Apps > Installed apps, then try again.',
    ],
    expectedResult:
      'Real-time protection shows ON and Windows can block malware the moment it tries to run.',
    avoid: [
      'Do not install random "free antivirus" or "PC cleaner" tools from pop-up ads.',
      'Do not disable Defender to make a download work — that download is almost certainly the problem.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 3,
    technicianNeeded: false,
  },

  avConflict: {
    meaning:
      'More than one antivirus is installed. They fight each other, which slows the PC and can leave gaps in protection.',
    steps: [
      'Open Settings > Apps > Installed apps.',
      'Find every third-party antivirus (e.g. McAfee, Norton, Avast, AVG, Kaspersky).',
      'Pick ONE to keep — for most home users, the built-in Windows Security is enough.',
      'Uninstall the others one at a time.',
      'Restart the PC.',
      'Open Windows Security and confirm "Virus & threat protection" shows a single active provider.',
    ],
    expectedResult:
      'Only one antivirus is listed as active. CPU and disk usage drop noticeably.',
    avoid: [
      'Do not uninstall an antivirus by deleting its folder — always use the official uninstaller.',
      'Do not leave trial antivirus software that came pre-installed if you are not paying for it.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 15,
    technicianNeeded: false,
  },

  highRamUsage: {
    meaning:
      'Your PC is using almost all of its memory. When RAM fills up, Windows swaps data to the much slower hard drive, which is why everything feels sluggish.',
    steps: [
      'Press Ctrl + Shift + Esc to open Task Manager.',
      'Click the "Memory" column to sort by highest usage.',
      'Close apps you are not using (especially browsers with many tabs).',
      'Right-click the Taskbar > Task Manager > Startup apps. Disable items you do not need at boot.',
      'Restart the PC to clear leaked memory.',
      'If usage stays above 85% with nothing open, consider adding more RAM.',
    ],
    expectedResult:
      'RAM usage drops below 75% during normal use and apps respond immediately.',
    avoid: [
      'Do not install "RAM booster" or "memory cleaner" apps — they make things worse.',
      'Do not increase the page file size unless a technician tells you to.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 10,
    technicianNeeded: false,
  },

  lowAvailRam: {
    meaning:
      'Very little free RAM is left for new apps. Opening anything else will be slow or may fail.',
    steps: [
      'Save your work first.',
      'Close the heaviest apps (browser, video editor, games).',
      'Press Ctrl + Shift + Esc > Memory column to see who is using the most.',
      'Restart the PC if free RAM stays low after closing apps.',
      'If this happens every day, plan a RAM upgrade (typically 8 GB → 16 GB).',
    ],
    expectedResult:
      'At least 2-3 GB of RAM is free during normal use.',
    avoid: [
      'Do not force-kill system processes (anything labelled "Windows" or "System") — you can crash the PC.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 5,
    technicianNeeded: false,
  },

  browserMemory: {
    meaning:
      'Your web browser is the biggest memory consumer. Modern browsers treat every tab and extension as its own program.',
    steps: [
      'Close tabs you are not actively reading.',
      'In the browser menu, open Extensions and remove ones you do not recognize or no longer use.',
      'In Chrome/Edge: open Settings > Performance > turn on "Memory Saver".',
      'Restart the browser fully (close every window, then reopen).',
      'If the browser still uses excessive RAM, try a clean profile or reinstall it.',
    ],
    expectedResult:
      'Browser memory use drops by 30-50% and tab switching feels instant again.',
    avoid: [
      'Do not install "tab manager" or "speed booster" extensions — they usually make memory use worse.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 5,
    technicianNeeded: false,
  },

  lowDiskC: {
    meaning:
      'Your C: drive is nearly full. Windows needs free space for updates, temp files, and the page file. Below 10-15 GB free, the whole PC will start to lag.',
    steps: [
      'Open Start > search "Storage settings" > open it.',
      'Click "Temporary files" and tick everything except "Downloads" (only tick that if you have backed them up).',
      'Click "Remove files".',
      'Open Settings > Apps > Installed apps and sort by size. Uninstall things you no longer use.',
      'Empty the Recycle Bin.',
      'Move large folders (Videos, Photos) to an external drive or OneDrive if needed.',
    ],
    expectedResult:
      'At least 20 GB free on C:. Windows Update and apps start working normally again.',
    avoid: [
      'Do not delete files from C:\\Windows or C:\\Program Files directly — use the uninstaller.',
      'Do not use third-party "PC cleaner" tools that ask for payment to "fix" your PC.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 15,
    technicianNeeded: false,
  },

  startupApps: {
    meaning:
      'Too many programs launch automatically when Windows starts. Each one slows boot and uses memory all day.',
    steps: [
      'Right-click the Taskbar > "Task Manager".',
      'Click the "Startup apps" tab.',
      'Sort by "Startup impact" (High first).',
      'For each High-impact item you recognize and don\'t need at boot (Spotify, Steam, Discord, Teams, OneDrive helpers, printer software, etc.), click it and choose "Disable".',
      'Leave anything labelled "Microsoft" or "Windows" alone.',
      'Restart the PC.',
    ],
    expectedResult:
      'Boot time drops noticeably and the PC is usable within seconds of reaching the desktop.',
    avoid: [
      'Do not disable items you don\'t recognize without a quick Google search first.',
      'Do not use "startup optimizer" tools — Task Manager already does this safely.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 10,
    technicianNeeded: false,
  },

  windowsUpdate: {
    meaning:
      'Windows Update is behind. Missing updates means missing security patches and bug fixes that affect stability.',
    steps: [
      'Open Settings > Windows Update.',
      'Click "Check for updates".',
      'Install everything offered, including optional driver updates.',
      'Restart when prompted (use "Restart now", not "Shut down").',
      'After reboot, run "Check for updates" again — sometimes more appear.',
    ],
    expectedResult:
      'Windows Update shows "You\'re up to date" with no pending restart.',
    avoid: [
      'Do not shut the PC down during an update — let it finish.',
      'Do not pause updates for weeks at a time.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 30,
    technicianNeeded: false,
  },

  pendingReboot: {
    meaning:
      'Windows has applied updates that need a restart to finish. Until you reboot, some drivers and patches are not actually active.',
    steps: [
      'Save and close everything you are working on.',
      'Click Start > Power > Restart (not Shut down).',
      'Wait for Windows to finish "Working on updates" — this can take 5-20 minutes.',
      'Sign back in and confirm Settings > Windows Update no longer says "Restart required".',
    ],
    expectedResult:
      'No "Restart required" banner. Recent updates are fully applied.',
    avoid: [
      'Do not hold the power button to force shutdown during the restart — that can corrupt the update.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 15,
    technicianNeeded: false,
  },

  biosUpdate: {
    meaning:
      'Your BIOS/firmware controls how Windows talks to the hardware. An old BIOS can cause crashes, slow boot, or stop new hardware from working.',
    steps: [
      'On a Dell: open Start > search and install "Dell SupportAssist", launch it, then click "Get drivers & downloads" — let it install BIOS and critical drivers.',
      'On an HP: install "HP Support Assistant" from the Microsoft Store, then run Updates.',
      'On a Lenovo: install "Lenovo Vantage" from the Microsoft Store, then run System Update.',
      'Plug the laptop into mains power before starting — do not run a BIOS update on battery.',
      'Do not interrupt the BIOS update; the PC will reboot itself, possibly more than once.',
    ],
    expectedResult:
      'Vendor tool reports BIOS is current. System feels more stable, especially on boot and resume from sleep.',
    avoid: [
      'Do not download BIOS files from unofficial sites or driver-pack tools.',
      'Do not update BIOS over WiFi if you can plug in Ethernet — a dropped download can brick the PC.',
    ],
    difficulty: 'medium' as FixDifficulty,
    timeMinutes: 30,
    technicianNeeded: false,
  },

  driverUpdates: {
    meaning:
      'One or more drivers are flagged as problematic or out of date. Bad drivers are a common cause of crashes, WiFi drops, and graphics glitches.',
    steps: [
      'Open Settings > Windows Update > Advanced options > Optional updates > Driver updates. Install everything listed.',
      'For laptops, also run the vendor tool (Dell SupportAssist / HP Support Assistant / Lenovo Vantage / MyASUS) to catch drivers Windows Update misses.',
      'For graphics drivers specifically, use NVIDIA GeForce Experience, AMD Adrenalin, or Intel Driver & Support Assistant.',
      'Restart after each major driver install.',
      'Open Device Manager (Win + X > Device Manager) and confirm there are no yellow warning icons.',
    ],
    expectedResult:
      'Device Manager is clean. Crashes, WiFi drops, or display glitches related to drivers stop.',
    avoid: [
      'Do not use "Driver Booster", "DriverPack Solution", or similar — they bundle wrong or malicious drivers.',
      'Do not roll back a driver unless the new one is clearly causing a new problem.',
    ],
    difficulty: 'medium' as FixDifficulty,
    timeMinutes: 30,
    technicianNeeded: false,
  },

  wifiIssues: {
    meaning:
      'Your WiFi is dropping, slow, or running an old driver. This affects video calls, streaming, and downloads.',
    steps: [
      'Move closer to the router as a quick test.',
      'On the laptop, open Settings > Network & Internet > Advanced network settings > Network reset (this re-installs the WiFi adapter).',
      'Restart the router: unplug it for 30 seconds, plug back in, wait 2 minutes.',
      'Update the WiFi driver via Windows Update > Optional updates > Driver updates, OR via the laptop vendor tool.',
      'If you have a dual-band router, connect to the 5 GHz network for speed (close to the router) or 2.4 GHz for range (far from the router).',
      'If problems persist, change the router\'s WiFi channel in the router admin page.',
    ],
    expectedResult:
      'Stable connection with no random drops and link speed close to what your plan provides.',
    avoid: [
      'Do not install "WiFi booster" software — none of it works.',
      'Do not change advanced adapter settings (roaming aggressiveness etc.) unless guided.',
    ],
    difficulty: 'medium' as FixDifficulty,
    timeMinutes: 20,
    technicianNeeded: false,
  },

  bsodCrashes: {
    meaning:
      'Your PC has had blue-screen crashes. These are caused by a faulty driver, failing hardware, or corrupted system files — not by Windows itself.',
    steps: [
      'Open Settings > Windows Update and install every update including optional driver updates.',
      'Run the vendor tool (Dell SupportAssist etc.) and apply BIOS + driver updates.',
      'Open Start > search "Reliability Monitor" > review the timeline for the day the crash happened. Note which app or driver failed.',
      'Open an admin Command Prompt and run: sfc /scannow then dism /online /cleanup-image /restorehealth — let both finish.',
      'If crashes continue, run Windows Memory Diagnostic (Start > search it) to test RAM overnight.',
      'If memory and disk tests come back clean and crashes continue, contact a technician with the crash dates — the hardware likely needs servicing.',
    ],
    expectedResult:
      'No further blue screens. Reliability Monitor stays clean for at least a week.',
    avoid: [
      'Do not run BlueScreenView or "BSOD fixer" tools from unknown sites — they often install adware.',
      'Do not reinstall Windows as a first step; fix the driver or hardware first.',
    ],
    difficulty: 'hard' as FixDifficulty,
    timeMinutes: 60,
    technicianNeeded: true,
  },

  batteryWear: {
    meaning:
      'Your battery has lost a significant chunk of its original capacity. It will not last as long on a charge and may shut the laptop down unexpectedly when low.',
    steps: [
      'Open Settings > System > Power & battery and review the battery usage chart.',
      'Avoid leaving the laptop plugged in at 100% for days at a time.',
      'If your laptop vendor offers a "battery health" or "conservation mode" (Lenovo, ASUS, Dell), enable it to cap charging at ~80%.',
      'For everyday use, top up between 20% and 80% rather than full cycles.',
      'When wear exceeds 30-40% and runtime becomes painful, plan a battery replacement (vendor service or authorised repair).',
    ],
    expectedResult:
      'Battery wear stops getting worse. Runtime stabilises at a predictable level.',
    avoid: [
      'Do not use "battery calibration" tools from random sites.',
      'Do not let the battery die to 0% repeatedly — it accelerates wear.',
    ],
    difficulty: 'medium' as FixDifficulty,
    timeMinutes: 60,
    technicianNeeded: true,
  },

  secureBootOff: {
    meaning:
      'Secure Boot is turned off. It is a firmware feature that blocks rootkits and tampered boot loaders from loading before Windows starts.',
    steps: [
      'Save your work and restart the PC.',
      'As it restarts, press the BIOS key for your model (Dell: F2, HP: F10, Lenovo: F1 or Enter then F1, ASUS: F2 or Del).',
      'Go to the "Boot" or "Security" section.',
      'Find "Secure Boot" and set it to "Enabled".',
      'Save & Exit (usually F10).',
      'Boot back into Windows. Open System Information (Start > msinfo32) and confirm "Secure Boot State" is "On".',
    ],
    expectedResult:
      'msinfo32 shows Secure Boot State: On. No change in day-to-day use.',
    avoid: [
      'Do not change any other BIOS setting while you are in there.',
      'Do not enable Secure Boot if you are dual-booting an older Linux distro without first checking it supports Secure Boot.',
    ],
    difficulty: 'medium' as FixDifficulty,
    timeMinutes: 10,
    technicianNeeded: false,
  },

  tpmOff: {
    meaning:
      'TPM (Trusted Platform Module) is disabled. Windows uses it for BitLocker, Windows Hello, and several security features. Windows 11 expects it on.',
    steps: [
      'Restart the PC and press the BIOS key (Dell: F2, HP: F10, Lenovo: F1, ASUS: F2/Del).',
      'Look in "Security" or "Advanced" for an option called "TPM", "PTT" (Intel Platform Trust Technology), or "fTPM" (AMD).',
      'Set it to Enabled.',
      'Save & Exit.',
      'In Windows, press Win + R, type "tpm.msc" and press Enter. Confirm it shows "The TPM is ready for use".',
    ],
    expectedResult:
      'tpm.msc reports TPM is ready. BitLocker and Windows Hello are usable.',
    avoid: [
      'Do not "clear" the TPM if BitLocker is already protecting the drive — you will need the 48-digit recovery key to get back in.',
    ],
    difficulty: 'medium' as FixDifficulty,
    timeMinutes: 10,
    technicianNeeded: false,
  },

  firewallOff: {
    meaning:
      'Windows Firewall is off for at least one network profile. Without it, anything on the same WiFi can attempt direct connections to your PC.',
    steps: [
      'Open Start > search "Windows Security" and open it.',
      'Click "Firewall & network protection".',
      'For each profile listed (Domain, Private, Public), click it and switch "Microsoft Defender Firewall" ON.',
      'Go back; all three profiles should now show a green tick.',
      'If a third-party security suite is installed and managing the firewall, confirm its firewall is enabled instead.',
    ],
    expectedResult:
      'All network profiles show firewall ON.',
    avoid: [
      'Do not disable the firewall to "fix" a game or app — instead create an inbound rule for that specific program.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 3,
    technicianNeeded: false,
  },

  stale: {
    meaning:
      'Your antivirus definitions are out of date, so the latest known malware cannot be recognised.',
    steps: [
      'Open Windows Security > Virus & threat protection.',
      'Under "Virus & threat protection updates", click "Check for updates" then "Check for updates" again.',
      'Wait for definitions to download and apply.',
      'Run a Quick scan once updates finish.',
    ],
    expectedResult:
      'Definitions show today\'s date. Quick scan reports no threats.',
    avoid: [
      'Do not stop the "Microsoft Defender Antivirus Service" — it is what downloads the updates.',
    ],
    difficulty: 'easy' as FixDifficulty,
    timeMinutes: 5,
    technicianNeeded: false,
  },
};

const FIX_GUIDANCE: Record<string, FixGuidance> = {
  // Security
  'SEC-001': G.realtimeOff,
  'SEC-002': G.realtimeOff,         // Tamper protection — same flow
  'SEC-003': G.stale,
  'SEC-004': G.realtimeOff,
  'SEC-005': G.realtimeOff,
  'WIN-004': G.avConflict,
  'WIN-005': G.firewallOff,
  'WIN-007': G.stale,

  // System / Firmware
  'SYS-007': G.tpmOff,
  'SYS-008': G.secureBootOff,

  // Windows update
  'WIN-001': G.windowsUpdate,
  'WIN-002': G.pendingReboot,
  'WIN-008': G.windowsUpdate,

  // RAM
  'RAM-002': G.highRamUsage,
  'RAM-003': G.lowAvailRam,
  'RAM-007': G.highRamUsage,
  'RAM-008': G.browserMemory,

  // Storage
  'STO-001': G.lowDiskC,
  'STO-008': G.lowDiskC,
  'STO-009': G.lowDiskC,
  'STO-004': G.bsodCrashes,         // SMART failure — escalate

  // Startup
  'STA-001': G.startupApps,
  'STA-002': G.startupApps,
  'STA-003': G.startupApps,
  'STA-004': G.startupApps,
  'STA-006': G.startupApps,

  // Drivers / firmware
  'DRV-001': G.driverUpdates,
  'DRV-002': G.driverUpdates,
  'DRV-003': G.driverUpdates,
  'DRV-005': G.driverUpdates,

  // Network
  'NET-002': G.wifiIssues,
  'NET-003': G.wifiIssues,
  'NET-005': G.wifiIssues,
  'NET-006': G.wifiIssues,
  'NET-007': G.wifiIssues,

  // Stability
  'BSD-001': G.bsodCrashes,
  'BSD-002': G.bsodCrashes,
  'BSD-003': G.bsodCrashes,
  'BSD-004': G.bsodCrashes,
  'EVT-001': G.bsodCrashes,
  'EVT-003': G.bsodCrashes,

  // Battery
  'BAT-002': G.batteryWear,
  'BAT-005': G.startupApps,         // Power plan tweak shares vibe

  // CPU thermal — escalate
  'CPU-006': G.bsodCrashes,
};

/**
 * Look up plain-English fix guidance for a check by ID. Returns null if no
 * tailored template exists (caller should fall back to the check's built-in
 * `recommendation` field).
 */
export function getFixGuidance(checkId: string): FixGuidance | null {
  return FIX_GUIDANCE[checkId] || null;
}

/**
 * Suggest BIOS / vendor-tool update steps based on detected manufacturer.
 * Useful as a top-level Pro card.
 */
export function getVendorUpdateGuidance(manufacturer: string): FixGuidance {
  const m = (manufacturer || '').toLowerCase();
  const base = { ...G.biosUpdate };
  if (m.includes('dell')) {
    base.steps = [
      'Open Start menu > search "Dell SupportAssist". If it is not installed, download it from dell.com/support.',
      'Launch SupportAssist and click "Get drivers & downloads".',
      'Let it scan, then install everything it offers — especially anything labelled "BIOS" or "Urgent".',
      'Plug the laptop into mains power before starting the BIOS update.',
      'Allow it to reboot. The screen may flash or go black for a few minutes — do not interrupt.',
      'Once back in Windows, run SupportAssist again to confirm "No updates available".',
    ];
  } else if (m.includes('hp')) {
    base.steps[0] = 'Open the Microsoft Store and install "HP Support Assistant".';
    base.steps[1] = 'Launch HP Support Assistant and click "Updates".';
  } else if (m.includes('lenovo')) {
    base.steps[0] = 'Open the Microsoft Store and install "Lenovo Vantage".';
    base.steps[1] = 'Launch Lenovo Vantage and click "System Update".';
  } else if (m.includes('asus')) {
    base.steps[0] = 'Open the Microsoft Store and install "MyASUS".';
    base.steps[1] = 'Launch MyASUS, go to Customer Support > Live Update.';
  }
  return base;
}

